import React, { useContext, Fragment, useEffect, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expVal } from '@confluence/feature-experiments';
import {
	createRouteParamHook,
	useHistory,
	useRouteDataRef,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { withDocumentTitle } from '@confluence/document-title/entry-points/with-document-title.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import { SpaceBaseThemedComponent } from './SpaceBaseThemedComponent.tsx';
import { SpaceRestrictionHandler } from './SpaceRestrictionHandler.tsx';
import { useSpaceBaseQuery } from './useSpaceBaseQuery.tsx';
import type { useSpaceBaseQueryBaseQuery$data } from './__generated__/useSpaceBaseQueryBaseQuery.graphql.ts';

type Space = NonNullable<
	NonNullable<NonNullable<useSpaceBaseQueryBaseQuery$data['spaces']>['nodes']>[number]
>;

type SpaceBaseComponentProps = {
	children: React.ReactNode;
	spaceKey: string;
	isSpaceLoading: boolean;
	space?: Space | null;
};

type SpaceBaseProps = PropsWithChildren<{
	spaceKey?: string;
}>;

const DISPLAY_ROOT = 'display';
const SPACE_ROOT = 'spaces';

const getUrlRoot = (pathname: string | undefined) => {
	if (!pathname) {
		return undefined;
	}
	if (pathname.includes(`/${SPACE_ROOT}/`)) {
		return SPACE_ROOT;
	} else if (pathname.includes(`/${DISPLAY_ROOT}/`)) {
		return DISPLAY_ROOT;
	} else {
		return undefined;
	}
};
const useContentSlug = createRouteParamHook('contentSlug');
const useContentId = createRouteParamHook('contentId');

const LegacySpaceBaseComponent = ({
	children,
	spaceKey,
	space,
	isSpaceLoading,
}: SpaceBaseComponentProps) => {
	const contentId = useContentId();
	const contentSlug = useContentSlug();
	const isViewPage = !!contentId || !!contentSlug;
	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isAnonymous } = useContext(SPAViewContext);
	const history = useHistory();
	const isFirstRoute = history?.length === 1;
	const location = useLocation();

	const fixSpaceEncodingInSpaceKey = () => {
		const pathname = location?.pathname || '';
		const decodedPathname = decodeURIComponent(pathname);

		if (
			// excludes the cases where there are no + symbols to replace at all
			// eg: /spaces/perfectly-fine-space-key
			pathname.indexOf('+') !== -1 &&
			// excludes the cases where there is a + that follows the space key portion of the path
			// eg: /spaces/DS/other+stuff+that+contains+plus
			spaceKey.indexOf('+') !== -1 &&
			// excludes the cases where the decoded path name still contains a + (from %2B)
			// , but the replacement to strip the + from the space key has already happened in previous SPA transition
			// eg: pathname = '/spaces/space%2Bkey', decodedPathname = '/spaces/space+key', spaceKey = 'space+key'
			decodedPathname.indexOf(`spaces/${spaceKey}`) !== -1
		) {
			history?.replace?.({
				...location,
				// Ensure we are replacing the "+" in the space key not other funny stuff
				pathname: decodedPathname.replace(
					`spaces/${spaceKey}`,
					`spaces/${spaceKey}`.replace(/\+/g, ' '),
				),
			});
		}
	};

	const removeEmailQueryParameters = () => {
		if (!location?.search?.includes('src=mail') && !location?.search?.includes('src.mail.')) {
			return;
		}

		let search = location?.search?.substring(1);
		const params = search
			.split('&')
			.filter((param) => !param.includes('src=mail') && !param.startsWith('src.mail.'));
		search = `?${params.join('&')}`;

		history?.replace?.({
			...location,
			search,
		});
	};

	useEffect(() => {
		fixSpaceEncodingInSpaceKey();
		removeEmailQueryParameters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spaceKey]);

	useEffect(() => {
		if (isSpaceLoading) return;
		if (space && space.alias && space.alias !== spaceKey) {
			const pathnameRoot = getUrlRoot(location?.pathname);
			if (pathnameRoot) {
				const routeName = routeDataRef.current?.routeName || null;

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'redirect',
						actionSubject: 'route',
						source: 'SpaceBase',
						attributes: {
							routeName,
							// Space key related attributes.
							areSpaceKeysEqualLowercase: space?.alias?.toLowerCase() === spaceKey?.toLowerCase(),
						},
					},
				}).fire();

				history?.replace?.({
					...location,
					pathname: location?.pathname?.replace(
						`/${pathnameRoot}/${spaceKey}`,
						`/${pathnameRoot}/${space.alias}`,
					),
				});
			}
			return;
		}
	}, [createAnalyticsEvent, history, location, space, spaceKey, routeDataRef, isSpaceLoading]);
	/*
	 * Removing SpaceBaseThemedComponent for the Company Hub app-like experience
	 *
	 * @see {@link https://www.figma.com/design/MBdUXhdmgbsIkMkOhQGu5e/Hub-App?node-id=101-52260}
	 * @see {@link https://hello.atlassian.net/wiki/spaces/CLI/pages/4856024337/Confluence-less+Team+25+US+Experience}
	 */
	const SpaceBaseWrapper = isCompanyHubSpaceKey(spaceKey) ? Fragment : SpaceBaseThemedComponent;

	return (
		<SpaceRestrictionHandler
			spaceKey={spaceKey}
			isAnonymous={isAnonymous}
			contentId={contentId}
			isFirstRoute={isFirstRoute}
			goBackPreviousPage={history?.goBack}
		>
			<div
				data-testid="space-base-wrapper"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={classnames({
					'wrapper-space-view-page': isViewPage,
					'wrapper-space-home-page': !isViewPage,
				})}
			>
				<SpaceBaseWrapper>{children}</SpaceBaseWrapper>
			</div>
		</SpaceRestrictionHandler>
	);
};

const SpaceBaseComponent = ({
	children,
	spaceKey,
	space,
	isSpaceLoading,
}: SpaceBaseComponentProps) => {
	const contentId = useContentId();
	const contentSlug = useContentSlug();
	const isViewPage = !!contentId || !!contentSlug;
	const { isAnonymous } = useContext(SPAViewContext);

	// We cannot use useRenderServerPlaceholder() here
	// because SpaceBaseComponent is invoked through withDocumentTitle's inline
	// function pattern, which breaks hooks during renderToString.
	const isSSROrHydrating = expVal(
		'confluence_hydration_with_document_title_fix',
		'isEnabled',
		false,
	)
		? // eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR) || Boolean((window as any).__HYDRATABLE__)
		: // eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR);

	if (isSSROrHydrating && expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)) {
		return (
			<SpaceRestrictionHandler
				spaceKey={spaceKey}
				isAnonymous={isAnonymous}
				contentId={contentId}
				goBackPreviousPage={() => {}}
			>
				<div
					data-testid="space-base-wrapper"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={classnames({
						'wrapper-space-view-page': isViewPage,
						'wrapper-space-home-page': !isViewPage,
					})}
				>
					{children}
				</div>
			</SpaceRestrictionHandler>
		);
	} else if (isSSROrHydrating) {
		return (
			<div
				data-testid="space-base-wrapper"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={classnames({
					'wrapper-space-view-page': isViewPage,
					'wrapper-space-home-page': !isViewPage,
				})}
			>
				{children}
			</div>
		);
	} else {
		return LegacySpaceBaseComponent({
			children,
			spaceKey,
			space,
			isSpaceLoading,
		});
	}
};

const useSpaceKey = createRouteParamHook('spaceKey');

export const SpaceBase = ({ children, spaceKey }: SpaceBaseProps) => {
	const resolvedSpaceKey = useSpaceKey() || spaceKey || '';
	const { space, loading } = useSpaceBaseQuery({ resolvedSpaceKey });

	return withDocumentTitle(() => space?.name)(SpaceBaseComponent)({
		spaceKey: resolvedSpaceKey,
		space,
		children,
		isSpaceLoading: loading,
	});
};
